import { Injectable } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { TsTableService } from 'tsui/lib/services/ts-table.service';
import { ColumnDef, OptionsFilterFn } from 'tsui/lib/ts-table/ts-table.model';
import { TableFileUploadEditorComponent } from '@shared/components/ts-table-renderers/editors/table-file-upload-editor.component';
import { TableFileResourceRendererComponent } from '@shared/components/ts-table-renderers/table-file-resource-renderer.component';
import { AccountService } from '@core/services/account.service';
import formatNumber from 'accounting-js/lib/formatNumber.js';

@Injectable()
export class ProjectTsTableService extends TsTableService {
  constructor(private readonly accountService: AccountService) {
    super();
  }

  private shouldConvertDaysToYears(fieldDef: ColumnDef): boolean {
    const accountId = Number(this.accountService.getCurrentAccount()?.id);
    //SC-55770, Added the handling for loan terms for ambient capital
    return accountId === 1570 && fieldDef?.name === 'cf_loan_term_7';
  }

  private formatDaysAsYears(value: any, precision = 2): string {
    if (value === undefined || value === null || value === '') return '';
    const days = parseFloat(value);
    if (isNaN(days)) return '';
    const years = days / 365;
    const label: string = years > 1 ? 'years' : 'year'
    return formatNumber(years, { precision }) + ' ' + label;
  }

  // Used by TsTableComponent when building columns
  override setValueFormatter(
    fieldDef: ColumnDef,
    optionsFilterFn: OptionsFilterFn = null,
  ): (params: any) => string {
    if (this.shouldConvertDaysToYears(fieldDef)) {
      return (params) => {
        const raw = this.getValueFromParam(fieldDef, params);
        return this.formatDaysAsYears(raw);
      };
    }

    return super.setValueFormatter(fieldDef, optionsFilterFn);
  }

  // Used by various tables that call the selector form directly
  override setValueFormatterSelector(
    fieldDef: ColumnDef,
    relatedToRedirectConfig?: any,
    data?: any,
  ): (params: any) => string {
    if (this.shouldConvertDaysToYears(fieldDef)) {
      return (params) => this.formatDaysAsYears(params?.value);
    }

    return super.setValueFormatterSelector(fieldDef, relatedToRedirectConfig, data);
  }
  
  protected override getRecordTypeCellEditor(
    fieldDef: ColumnDef,
    relatedToRedirectConfig: any,
    data?: any,
    optionsFilterFn: OptionsFilterFn = null,
  ): Partial<ColDef> | null {
    if (fieldDef?.meta?.record_type !== 'FileResource') return null;
    return {
      cellEditor: TableFileUploadEditorComponent,
      cellEditorParams: {
        multiSelect: fieldDef.meta?.multi_select,
        recordType: fieldDef.meta?.record_type,
        options: fieldDef.meta?.options,
        allowedTypes: (fieldDef as any)?.meta?.allowed_types,
      },
      cellRenderer: TableFileResourceRendererComponent,
    } as Partial<ColDef>;
  }

  protected override getRecordTypeCellEditorSelector(
    fieldDef: ColumnDef,
    relatedToRedirectConfig: any,
    data?: any,
  ): any | null {
    if (fieldDef?.meta?.record_type !== 'FileResource') return null;
    return {
      component: TableFileUploadEditorComponent,
      params: {
        multiSelect: fieldDef.meta?.multi_select,
        recordType: fieldDef.meta?.record_type,
        options: fieldDef.meta?.options,
      },
    };
  }

  protected override getRecordTypeCellRendererSelector(
    fieldDef: ColumnDef,
    relatedToRedirectConfig?: any,
    data?: any,
  ): any | null {
    if (fieldDef?.meta?.record_type !== 'FileResource') return null;
    return { component: TableFileResourceRendererComponent };
  }
}
